/**
 * Packed-int hashing — the ONLY randomness source for region-cell worldgen.
 *
 * String seeds (xmur3/mulberry32 in rng.ts) remain the universe's identity
 * scheme above planet level. Inside a planet, every generator draws from
 * hashCell over integer cell coordinates so that (a) hot paths never touch
 * strings and (b) any cell's stream is computable in O(1) with no draw-order
 * bookkeeping. planetHash = hashSeed(planet.seed) bridges the two worlds.
 */
import { mulberry32, type Rng } from "../rng.js";

/** One 32-bit avalanche round folding `k` into state `h`. */
function mix(h: number, k: number): number {
  h = Math.imul(h ^ (k | 0), 2654435761);
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519);
  return h ^ (h >>> 13);
}

/** Hash up to six small ints into a uint32. Order-sensitive by construction. */
export function hashInts(a: number, b: number, c = 0, d = 0, e = 0, f = 0): number {
  let h = (a | 0) ^ 0x9e3779b9;
  h = mix(h, b);
  h = mix(h, c);
  h = mix(h, d);
  h = mix(h, e);
  h = mix(h, f);
  h = Math.imul(h ^ (h >>> 16), 3266489917);
  return (h ^ (h >>> 15)) >>> 0;
}

/**
 * Salt registry — every hashInts stream domain in worldgen. Distinct constants
 * keep stages independent; NEVER reuse a value below for a new stream.
 *
 *   level domain (hashCell / edgeKey 2nd arg):
 *     0 = province level, 1 = region level
 *   boundary.ts:
 *     0x0edce  edge keys (5th arg of hashInts)
 *   fields.ts:
 *     0xa11    seed subgrid jitter (candidateDir) — STILL the candidate geometry
 *     0x6ee9   seed keep roll — RETIRED (W2c-1: quota deck replaced per-cell keep)
 *     0x512e   seed size class  — RETIRED (W2c-1: tier dealt from the quota deck)
 *     0x5eed   seed identity (settlement id)
 *     0xe7e    world events             0xf0d   settlement founding age
 *     0x6a7e   growth rate              0x9c05  prosperity base
 *   tierRegistry.ts (W2c-1 per-planet quota deck; per-PLANET rolls unless noted):
 *     0x3e7a   metropolis presence gate (~35% of qualifying planets)
 *     0x9074   quota count rolls — 3rd hashInts arg selects the tier's range
 *              (1 city, 2 town, 3 village, 4 hamlet)
 *     0xde1a   per-settlement deviation slot — keyed on (cellKey, k); f-arg 1
 *              rolls the id 1..4 after the <0.15 presence draw
 *     0x3a71   waystation carve-out — per CELL (face,x,y): f=0 presence (~0.02),
 *              f=1 placement subcell. Fires on ANY archetype.
 *     0x7a91   waystation dry-slot re-roll (W2d-2) — per CELL (face,x,y): if the
 *              dealt subcell is wet, f=attempt (1..4) rolls a fresh subcell; still
 *              wet after 4 → no waystation dealt.
 *   growth.ts placement (W2c-1 Task 2; per-PLANET rolls keyed on region cell + k):
 *     0x90b1   population roll within the primary settlement's tier range
 *     0x0b7a   optional-pool placement — f=0 membership (<OPTIONAL_PROB), f=1 pick
 *     0x3c15   waystation cluster building count (3..6)
 *     0x9e7f   REQUIRED-BUILDING fill (R1, warehouse regression) — when the road-
 *              derived parcels near a seed are FEWER than its required-use list,
 *              assignUses synthesizes deterministic square parcels around the seed
 *              for the unmet required uses so no required building is ever dropped.
 *              One draw per fill slot: `hashInts(planetHash, 0x9e7f, cellKey, seed.k,
 *              slotIndex)` picks the ring angle; radius steps outward per full ring.
 *              Placement geometry only (structures builds the box); the roll orders
 *              fill slots deterministically. Growth digest shifts (R1 epoch open).
 *   growth.ts:
 *     0xa77e   arterial meander — RETIRED (W2d-1 Task 2): the contour-streamline
 *              arterials it perturbed were deleted when A* primaries replaced them.
 *              Left listed (never re-use a retired value) so the primary/hamlet
 *              salts below cannot collide with it.
 *     0x5a2c   PRIMARY anchor selection — per (region cell, seed.k): the anchor
 *              COUNT roll (f-arg 0, range ANCHOR_COUNTS[tier]) plus the hashed
 *              high-suitability candidate points (f-arg 2·i+1 = radius √-jitter,
 *              2·i+2 = angle) that fill the anchor set beyond seed + ports.
 *     0x9d1a   PRIMARY road-count roll — one draw per VILLAGE+ seed, range
 *              PRIMARY_COUNTS[tier] (`hashInts(planetHash, 0x9d1a, packCell, k)`).
 *     0x1a7e   PRIMARY de-quantization JITTER (R1 D3) — per interior lattice node of
 *              an emitted primary polyline: keyed on the node's 20 m-lattice cell
 *              (`hashInts(planetHash, 0x1a7e, packCell, gi, gj, draw)`) so a node
 *              SHARED between two primaries jitters IDENTICALLY (trunk coincidence
 *              survives de-quantization). ONLY draw 0 is drawn = magnitude fraction in
 *              [0.35,0.6] of JITTER_MAX (2.1–3.6 m); the offset is PERPENDICULAR to the
 *              local tangent and its SIGN ALTERNATES by node parity (i&1) — a gentle
 *              wave, NOT a hashed direction (there is no draw-1 angle). Slope-aware +
 *              border-aware: the magnitude is scaled DOWN through the ladder
 *              [1, 0.6, 0.35, 0.18], taking the LARGEST scale whose two incident steps
 *              stay ≤ the grade cap AND keep the node inside its own cell (else the
 *              node is left at its lattice position).
 *     0xbe2d   PRIMARY straight-run-cap BEND (R1 D3; run-accumulating since R1 Task
 *              1.5) — a gentle perpendicular bend is inserted at each point where the
 *              ACCUMULATED straight-run length crosses a 120 m multiple (so a long run
 *              of short collinear lattice steps — e.g. a hamlet through-road — breaks
 *              every ~120 m, not only where a single segment exceeds the cap); keyed on
 *              the rounded bend point (`hashInts(planetHash, 0xbe2d, packCell,
 *              round(bendU), round(bendV), draw)`): draw 0 = amplitude (2..5 m),
 *              draw 1 = side sign. Both cells sharing a border segment round to the
 *              same point, so an inserted bend agrees cross-cell.
 *     0x4a3e   HAMLET building placement — per (region cell, seed.k, building b):
 *              building COUNT (f-arg 0, range HAMLET_BUILDINGS), then per building
 *              side (4·b+1), heading noise ±25° (4·b+2), offset 10–25 m (4·b+3).
 *     0x9707   RETIRED (W2d-2 Task 3): skeleton attractor scatter — the
 *              space-colonization grower it fed was deleted when the era'd tensor
 *              secondary growth replaced SC + densify. Left listed (never re-use a
 *              retired value); its XOR-with-k fan-out is gone with the SC loop.
 *     0xd15c   RETIRED (W2d-2 Task 3): district sub-pass scatter — deleted with the
 *              SC district pass (tensor eras replace the district banding).
 *     0x7e11   RETIRED (W2d-2 Task 3): slope type-switch tie-break (TUNNEL vs
 *              TERRACE) — only the SC `addTerrainEdge` + `densify` used it; the era
 *              grower emits EDGE_STREET only (width/type classes are W2d-3), so no
 *              stream draws it.
 *   streetgrow.ts (W2d-2 Task 3; the era'd tensor secondary grower — REPLACES SC):
 *     0x5ec0   secondary SEGMENT-LENGTH jitter — one draw per accepted segment
 *              (`hashInts(planetHash, 0x5ec0, cellKey, seed.k, segIndex)`), h01
 *              mapped to ±jitterFrac of the tier nominal length.
 *     0xb42c   BRANCH roll — per accepted segment: f=segIndex rolls the branch
 *              spawn (< branchProb), f-arg (segIndex,1) rolls the branch side ±90°.
 *     0x6d17   iteration-BUDGET roll — one draw per VILLAGE+/hamlet-lane settlement
 *              (`hashInts(planetHash, 0x6d17, cellKey, seed.k)`), h01 mapped into
 *              the tier's hard iteration-budget band (spec §"Core growth params").
 *              Heading noise reuses tensorfield's 0x4e15 (headingNoiseDeg).
 *     0xe1a5   era-COUNT roll — one draw per settlement whose spec era count is a
 *              RANGE (`hashInts(planetHash, 0xe1a5, cellKey, seed.k)`), h01 mapped
 *              into [eras, erasMax] (metropolis "4–5"). Fixed-count tiers never
 *              draw it. The outermost tensor band clamps (index ≤ 2), so a 5th era
 *              collapses onto the outer band — only the band BOUNDARIES shift.
 *     0x9a2c   parcel width jitter — ONE mulberry32 stream per region cell
 *              (rngFromCell, region level); emitParcels draws lot widths from it
 *              in fixed edge-ascending → side (front then back) → strip order,
 *              so the stream position is a pure function of the street graph
 *     0x77d1   RANGED class-width roll (W2d-3, v11) — one draw per
 *              (cell, class, tier) triple whose spec width entry is a RANGE
 *              (city primary 10–12, metro primary 14–18):
 *              `hashInts(planetHash, 0x77d1, cellKey, cls, tier)`, h01 lerped
 *              into [lo, hi]. Fixed-width entries never draw it.
 *     0x6b0c   ALLEY-chord roll (W2d-3 blocks) — one draw per (block face,
 *              sub-block): `hashInts(planetHash, 0x6b0c, cellKey, faceId,
 *              subIdx)` < alleyProb[tier] cuts an alley chord through the
 *              block. Oversized sub-blocks force the chord without drawing.
 *     0x415d   DISTRICT sectors (R3) — per settlement CELL: f=0 sector count
 *              (2..4), f=1 rotation; districtAt derives band × sector purely.
 *     0x5b4a   METRO SPRAWL pseudo-seed jitter (R3) — per (cell, direction d,
 *              attempt a): draws along-band (a*2) + across-band (a*2+1) offsets
 *              for the sprawl centre facing an adjacent metro; ≤6 wet re-rolls.
 *     0x1a4d   LANDMARK site roll (R3) — per (cell, seed.k): candidate i draws
 *              radius √-jitter (i*2) + angle (i*2+1) within 0.28 × tier radius;
 *              best suitability wins. Consumed by selectAnchors (anchor 1),
 *              plaza tie-break, and landmark placement — one pure derivation.
 *     0x62a7   PLAZA spawn roll (W2d-4) — one draw per TOWN settlement
 *              (`hashInts(planetHash, 0x62a7, cellKey, k)`) vs plazaProb.
 *     0x3a9d   MAIN-STREET count roll (W2d-4) — one draw per ranged-K settlement
 *              (metro 3-4): `hashInts(planetHash, 0x3a9d, cellKey, k)`.
 *     0x7fa1   TRAFFIC pair sampling (W2d-4) — two draws per sampled
 *              high-traffic pair: `hashInts(planetHash, 0x7fa1, cellKey, k,
 *              i*2 | i*2+1)`, h01 mapped over the endpoint set (desire lines).
 *     0x51c2   PARCEL-budget roll (v16) — one draw per settlement
 *              (`hashInts(planetHash, 0x51c2, cellKey, k)`), h01 → budget within
 *              the upper half of the tier's targetBuildings band (capParcels).
 *     0x107a   LOT cut-point jitter (W2d-3 blocks) — one draw per recursive
 *              subdivision cut: `hashInts(planetHash, 0x107a, cellKey, faceId,
 *              subIdx, path)` where `path` encodes the recursion-tree position;
 *              h01 mapped to mid ± skew of the OBB long axis.
 *   tensorfield.ts (W2d-2 Task 2; UNWIRED this phase — no generation call consumes
 *     these yet, so every worldgen digest HOLDS; Task 3 wires them into the grower):
 *     0xc0de   CULTURE-vector preset roll — one draw per settlement
 *              (`hashInts(planetHash, 0xc0de, cellKey, k)`), h01·4 → preset index
 *              0..3 (hieratic / blendA / blendB / grown), uniform.
 *     0x7e50   GRID orientation θ0 — one draw per settlement
 *              (`hashInts(planetHash, 0x7e50, cellKey, k)`), h01·(π/2) → the
 *              settlement-stable grid axis in [0, π/2) (grid axes are 90°-symmetric,
 *              so a quarter-turn covers every distinct orientation).
 *     0x4e15   HEADING-noise per segment — one draw per grown segment
 *              (`hashInts(planetHash, 0x4e15, cellKey, k, segIndex, saltStream)`),
 *              h01 mapped to a uniform deviation in ±headingNoiseBoundDeg. The
 *              caller-supplied saltStream (6th arg) fans out independent noise
 *              streams (e.g. per front / retry) without a new salt.
 *   structures.ts (shared building/wall builder, W2b-2.5):
 *     0xc17e   RETIRED (W2c-1 Task 2): the old class-2 0.4 city-wall gate.
 *     0xda17   RETIRED (R1 D6, 2026-07-10): the town-wall danger-rating placeholder.
 *              Perimeter CITY walls are removed end-to-end (user finding: "we don't
 *              need walls around cities") — no settlement rolls this salt anymore.
 *              Constant kept reserved so the value is never re-issued; render/
 *              collision geometry only, so its removal does not shift any digest.
 *     0xb1d6   per-parcel building rolls, a DRAW-INDEXED sub-stream keyed on the
 *              region cell + parcel index (deterministic per parcel across
 *              rebuilds; render/collision geometry only — no gameplay/carve
 *              effect). Every draw is
 *              `hashInts(planetHash, 0xb1d6, packCell(regionId), parcelIndex, drawIndex)`
 *              with drawIndex the FIFTH hashInts arg (`e`):
 *                drawIndex 0 = building HEIGHT (4–9 m). Since hashInts(a,b,c,d) ≡
 *                    hashInts(a,b,c,d,0), this is BYTE-IDENTICAL to the original
 *                    single-draw height roll — adding draws below cannot move it.
 *                drawIndex 1 = roof type (pitched vs flat)   ┐ Task-5 building-kit
 *                drawIndex 2 = window count (2–4)            │ rolls, APPENDED after
 *                drawIndex 3 = chimney presence (~30%)       ┘ the height draw.
 *                drawIndex 4 = ARCHETYPE roll (procedural buildings, record field
 *                    13) — cumulative band-weighted table via archetypeFor;
 *                    IGNORED for landmark uses whose archetype is fixed (each
 *                    draw is an independent hash keyed by drawIndex — no stream
 *                    position, so an unused roll shifts nothing).
 *                drawIndex 5 = BUILDINGSEED (record field 14) — the stored uint32
 *                    the whole building-plan grammar hangs off (see 0x51ab).
 *              (Height moved from the client renderer into shared `buildStructures`
 *              in Task 1; the kit rolls were appended in Task 5; archetype +
 *              buildingSeed in the procedural-buildings Task 7 — stride 13 → 15,
 *              fields 0..12 byte-identical. The DRAW-ORDER
 *              RULE — new draws only at higher drawIndex — keeps heights stable;
 *              the settlementmesh oracle proves it by recomputing drawIndex 0.)
 *   streetlights.ts (shared streetlight placement, Task ST-1):
 *     0x51a7  streetlight placement — per (region cell, edge/node, step). Interval
 *             lamps along non-tunnel road edges (per-step keep/t/side/height rolls)
 *             plus junction/seed/port node lamps (node key offset 0x9000). Grounded
 *             on the carved surface; render geometry only — no carve/gameplay effect,
 *             not digested.
 *   buildings/plan.ts (procedural building grammar; NOT a planetHash stream):
 *     0x51ab   building-plan LAYER fan-out — `hashInts(buildingSeed, 0x51ab,
 *              layer)`: the FIRST arg is the per-building uint32 buildingSeed
 *              (record field 14, the 0xb1d6 drawIndex-5 draw), not planetHash.
 *              `layer` = 0 massing / 1 palette / 2 greeble / 3 wear / 4 wildcard
 *              (layerRng), so re-tuning one plan layer never reshuffles another.
 *   test harness (determinism-snapshot):
 *     0xd12    test directions
 *
 * XOR-DERIVED STREAM CEILING: the scatter/district salts are XORed with a small
 * index (0x9707 ^ seed.k, 0xd15c ^ nodeIndex) to fan out per-seed / per-core
 * streams. That collides with another registered salt only at a specific index
 * (e.g. 0x9707 ^ k == 0x9a2c, the parcel-width salt, at k == 0x9707 ^ 0x9a2c ==
 * 0x0d2b == 3371). Seeds-per-cell and district-core counts are in the tens,
 * orders of magnitude below that ceiling, so the XOR fan-out is collision-free
 * at current scales — but any new salt must be chosen so its low bits keep this
 * margin.
 */

/**
 * The worldgen cell hash: pure function of (planet, level, face, x, y, salt).
 * Distinct salts = independent streams per stage — adding a stage never
 * shifts another (the towns.ts stream-separation discipline, kept).
 */
export function hashCell(planetHash: number, level: number, face: number, x: number, y: number, salt: number): number {
  return hashInts(planetHash, level, face, x, y, salt);
}

/** uint32 → [0, 1). */
export function h01(h: number): number {
  return h / 4294967296;
}

/** A mulberry32 stream seeded from a cell hash — for bounded multi-draw stages. */
export function rngFromCell(planetHash: number, level: number, face: number, x: number, y: number, salt: number): Rng {
  return mulberry32(hashCell(planetHash, level, face, x, y, salt));
}
